import { useEffect, useMemo, useState } from 'react';
import type { AuditRecord } from '@sfc/sdk';
import { platform } from '../api/client';
import { Empty } from '../ui/Empty';
import { dateTime, relativeTime } from '../ui/format';
import { StatStrip } from '../ui/StatStrip';

const LABELS: Record<string, string> = {
  'membership.provisioned': 'Added a member',
  'membership.role_changed': 'Changed a role',
  'membership.revoked': 'Removed a member',
  'member.password_reset': 'Reset a passphrase',
  'user.password_changed': 'Changed their own password',
};

interface AuditPanelProps {
  organizationId: string;
  /**
   * Bumped by whatever just changed administrative state.
   *
   * The history is meant to answer "what just happened", so it reloads when
   * something happens rather than only when the page is opened.
   */
  revision: number;
}

/** What has been done in this organization, newest first. */
export function AuditPanel({ organizationId, revision }: AuditPanelProps): React.JSX.Element {
  const [records, setRecords] = useState<AuditRecord[] | undefined>();

  useEffect(() => {
    const controller = new AbortController();
    platform.staff
      .readAudit(organizationId, controller.signal)
      .then(setRecords)
      .catch(() => setRecords([]));
    return () => controller.abort();
  }, [organizationId, revision]);

  const stats = useMemo(() => {
    const rows = records ?? [];
    const today = new Date().toISOString().slice(0, 10);
    const recent = rows.filter((record) => record.occurredAt.slice(0, 10) === today).length;
    return [
      { label: 'Recorded', value: rows.length },
      { label: 'Today', value: recent, tone: recent > 0 ? ('up' as const) : undefined },
    ];
  }, [records]);

  return (
    <section className="card" aria-labelledby="audit-heading">
      <StatStrip stats={stats} testId="audit-stats" />

      <div className="panel">
        <div className="panel__head">
          <h2 id="audit-heading">Administrative history</h2>
        </div>

        {records && records.length === 0 ? (
          <Empty
            icon="audit"
            title="Nothing recorded yet"
            hint="Every change to staff access and roles is written here, and cannot be edited."
          />
        ) : null}

        <ol className="timeline" data-testid="audit-records">
          {(records ?? []).map((record) => (
            <li key={record.id} className="timeline__item">
              <span className="timeline__dot" aria-hidden="true" />
              <span className="member">
                <span className="member__name">{LABELS[record.action] ?? record.action}</span>
                <span className="member__email">{formatDetails(record.details)}</span>
              </span>
              <time className="row__sub" dateTime={record.occurredAt} title={dateTime(record.occurredAt)}>
                {relativeTime(record.occurredAt)}
              </time>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function formatDetails(details: Record<string, string | null>): string {
  const { previousRole, role } = details;
  if (previousRole && role) return `${previousRole} → ${role}`;
  if (role) return String(role);
  if (previousRole) return `was ${previousRole}`;
  return '';
}
